/* eslint max-lines-per-function: ["error", {max: 194}] -- needed for AnalyticsProvider size */
import type { PropsWithChildren } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import isEmpty from "lodash/isEmpty";
import { Mixpanel } from "mixpanel-react-native";
import type { MixpanelProperties } from "mixpanel-react-native";

import {
  AnalyticsFieldName,
  HomeAnalyticsEvent,
  PageViewAnalytics,
} from "@/constants/analytics/Analytics";
import { UserTabSelectorAnalytics } from "@/constants/analytics/Tabs";
import ProcessEnvironment from "@/constants/ProcessEnvironment";
import { getUserTreatmentPhase } from "@/hooks/usePatientStatus";
import useAppMonitor from "@/services/AppMonitor";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { getUserRole } from "@/utils/analytics.util";
import { isWeb } from "@/utils/platform.util";
import { getAge, parseProfileDateOfBirth } from "@/utils/profile/age.util";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MixpanelProps = Record<string, any>;

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined,
);

const AnalyticsProvider = ({ children }: PropsWithChildren) => {
  const [mixpanelInstance, setMixpanelInstance] = useState<Mixpanel>();
  const { captureMessage } = useAppMonitor();
  const {
    dateOfBirth: appUserDateOfBirth,
    externalId: appUserId,
    isSupport: isAppUserSupport,
    universalStatusCode: appUserUniversalStatusCode,
  } = useAppUserSelector();
  const hasTrackedHomePageViewRef = useRef(false);

  useEffect(() => {
    const isMissingToken = isEmpty(ProcessEnvironment.MixpanelToken);
    if (isMissingToken) {
      captureMessage(
        "Mixpanel unable to initialize. Mixpanel token is not provided",
      );
      return;
    }
    // Ternary required: Mixpanel constructor overloads need literal true/false (not boolean variable)
    const mixpanel = isWeb()
      ? new Mixpanel(ProcessEnvironment.MixpanelToken, true, false)
      : new Mixpanel(ProcessEnvironment.MixpanelToken, true, true);
    void mixpanel.init();
    setMixpanelInstance(mixpanel);
  }, [captureMessage]);

  const appUserTreatmentPhase = useMemo(() => {
    return getUserTreatmentPhase(appUserUniversalStatusCode);
  }, [appUserUniversalStatusCode]);

  const track = useCallback(
    // eslint-disable-next-line max-params
    (event: string, props?: MixpanelProps) => {
      // mixpanel-react-native SDK has no concept of a page, thus it does not auto-capture the URL; so we manually capture it here.
      const baseWebProps = isWeb()
        ? {
            // explicitly omit the query params, in case a deep link or user manual query param contains something like a patient UUID
            $current_url: `${globalThis.window.location.origin}${globalThis.window.location.pathname}`,
          }
        : {};

      const properties: MixpanelProperties = {
        // NOTE: we capture the app user's CURRENT universal status code / treatment phase here
        // with every mixpanel event, to preserve historical data, as their universal status may change over time

        // ie: this is necessary, as opposed to only setting this field on the mixpanel 'user attributes'
        // which would lose historical context of what their treatment status was at a given point in time / mixpanel event
        [AnalyticsFieldName.AppUserTreatmentPhase]: appUserTreatmentPhase,
        [AnalyticsFieldName.AppUserUniversalStatusCode]:
          appUserUniversalStatusCode,
        ...props,
        ...baseWebProps,
      };

      mixpanelInstance?.track(event, properties);
    },
    [mixpanelInstance, appUserTreatmentPhase, appUserUniversalStatusCode],
  );

  /**
   * Tracks an event when a user selector component is pressed.
   *
   * @param {UserSelectorEventProps} params - The parameters for the event.
   * @param {string} params.component - The name of the component where the event occurred, if different from the source.
   * @param {string} params.selectedUser - The user that was selected.
   * @param {string} params.source - The page or modal where the component is located.
   */
  const trackUserSelectorEvent = useCallback(
    ({ component, selectedUser, source }: UserSelectorEventProps) => {
      const eventComponent = component || source;

      track(UserTabSelectorAnalytics.Pressed, {
        [AnalyticsFieldName.Component]: eventComponent,
        [AnalyticsFieldName.SelectedUser]: selectedUser,
        [AnalyticsFieldName.Source]: source,
      });
    },
    [track],
  );

  const trackPageView = useCallback(
    // eslint-disable-next-line max-params
    (pageName: string, props?: MixpanelProps) => {
      // include page name in the event name to be in line with PT-1078
      const eventName = `${pageName} ${PageViewAnalytics.PageViewEventType}`;

      // include page name as an attribute to follow mixpanel best practices
      // (ie, to best support filtering, sorting, grouping, etc)
      // see: https://docs.mixpanel.com/docs/data-structure/events-and-properties#keep-events-as-actions
      track(eventName, {
        [PageViewAnalytics.PageViewPageNameProp]: pageName,
        ...props,
      });
    },
    [track],
  );

  const trackHomeTabViewed = useCallback(
    (props?: MixpanelProps) => {
      if (hasTrackedHomePageViewRef.current) {
        return;
      }
      hasTrackedHomePageViewRef.current = true;
      trackPageView(HomeAnalyticsEvent.HomeTabViewed, props);
    },
    [trackPageView],
  );

  const identify = useCallback(
    async (userIdentity: AnalyticsUserIdentity) => {
      const { age, role, userId } = userIdentity;
      await mixpanelInstance?.identify(userId);
      mixpanelInstance?.getPeople().set({
        $Age: age,
        $Role: role,
      });
    },
    [mixpanelInstance],
  );

  const updateUserInfo = useCallback(
    (userInfo: AnalyticsUserInfo) => {
      const {
        adminEpisodeStatus,
        gender,
        linkedPatientIds,
        numberOfLinkedPatients,
        numberOfSupports,
        universalStatusCode,
      } = userInfo;

      mixpanelInstance?.getPeople().set({
        $Admin_Episode_Status: adminEpisodeStatus,
        $Gender: gender,
        $Linked_Patient_IDs: linkedPatientIds,
        $Number_Of_Linked_Patients: numberOfLinkedPatients,
        $Number_Of_Supports: numberOfSupports,
        $Universal_Status_Code: universalStatusCode,
      });
    },
    [mixpanelInstance],
  );

  const reset = useCallback(() => {
    hasTrackedHomePageViewRef.current = false;
    mixpanelInstance?.reset();
  }, [mixpanelInstance]);

  // identify() is invoked immediately once appUserId is set to a non-empty value
  // doing so here, rather than in the Home Tab, or a specific _layout file,
  // ensures analytics are user-identified even during onboarding or any future screens that come up first
  useEffect(() => {
    // exit early since can't identify user without a user ID
    if (isEmpty(appUserId)) return;
    const role = getUserRole({ isSupport: isAppUserSupport });
    let age: number | undefined;
    if (appUserDateOfBirth) {
      age = getAge(parseProfileDateOfBirth(appUserDateOfBirth));
    }
    void identify({ age, role, userId: appUserId });
  }, [appUserId, appUserDateOfBirth, identify, isAppUserSupport]);

  // this memoization may seem unnecessary, but this prevents needless rerenders
  // (potentially on every single render) due to the literal object {} being used directly
  const analytics = useMemo(
    () => ({
      identify,
      reset,
      track,
      trackHomeTabViewed,
      trackPageView,
      trackUserSelectorEvent,
      updateUserInfo,
    }),
    [
      identify,
      reset,
      track,
      trackHomeTabViewed,
      trackPageView,
      trackUserSelectorEvent,
      updateUserInfo,
    ],
  );

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
};

const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error("useAnalytics must be used within a AnalyticsProvider");
  }
  return context;
};

export { AnalyticsProvider, useAnalytics };
export type { MixpanelProps };
